import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import Button from '@/components/Button'
import { cn } from "@/lib/utils" 
import { Calendar } from "@/components/ui/calendar"
import { Row } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { isAxiosError } from '@/api/business' 
import { OperationTable } from '../..'
import { updateOperation } from '@/api/business/operation'
import { operationKeys, operationSegmentKeys } from '@/queries/useOperationQuery' 
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from "@/components/ui/select" 
import { useEffect, useState } from 'react' 
import { useCompaniesQuery } from '@/queries/useCompaniesQuery'
import { useSupplierQuery } from '@/queries/useSupplierQuery' 
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { TypographyXS } from '@/components/ui/typography'
import VirtualizedCommand from '@/components/VirtualizedCommand'
import { Checkbox } from '@/components/ui/checkbox'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'

interface EditDialogProps {
    row: Row<OperationTable>
    isOpen: boolean
    onClose: () => void
}

const EditDialog = ({ row, isOpen, onClose }: EditDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    const [supplier, setSupplier] = useState(row.original.SK_FORNECEDOR)

    const { data: dataCompanies } = useCompaniesQuery()
    const { data: dataSupplier } = useSupplierQuery()

    const schema = z.object({ 
        SK_FORNECEDOR: z.number(),
        SK_EMPRESA_DESTINO: z.string(),
        DD_TEMPO_OPERACAO: z.string(),
        DD_CTE: z.number(),
        DD_EXIBIR_OPERACAO: z.number(),
        DD_LETRA_PEDIDO: z.string(),
        DT_VIGENCIA_INICIAL: z.date(),
        DT_VIGENCIA_FINAL: z.date(),
        DD_NUMERO_ROTA: z.string().max(4)
    })
 
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            SK_FORNECEDOR: row.original.SK_FORNECEDOR || 0,
            SK_EMPRESA_DESTINO: row.original.SK_EMPRESA_DESTINO || "",
            DD_TEMPO_OPERACAO: row.original.DD_TEMPO_OPERACAO || "", 
            DD_NUMERO_ROTA: row.original.DD_NUMERO_ROTA || "", 
            DD_CTE: row.original.DD_CTE ? 1 : 0,  
            DD_EXIBIR_OPERACAO: row.original.DD_EXIBIR_OPERACAO  ? 1 : 0,  
            DD_LETRA_PEDIDO: row.original.DD_LETRA_PEDIDO || "",
            DT_VIGENCIA_INICIAL: row.original.DT_VIGENCIA_INICIAL != null ? new Date(`${row.original.DT_VIGENCIA_INICIAL} 00:00:00`) : new Date(),
            DT_VIGENCIA_FINAL: row.original.DT_VIGENCIA_FINAL != null ? new Date(`${row.original.DT_VIGENCIA_FINAL} 00:00:00`) : new Date(),
        },
    })

    useEffect(() => {
        setSupplier(row.original.SK_FORNECEDOR) 
        form.reset({
            SK_FORNECEDOR: row.original.SK_FORNECEDOR || 0,
            SK_EMPRESA_DESTINO: row.original.SK_EMPRESA_DESTINO || "",
            DD_TEMPO_OPERACAO: row.original.DD_TEMPO_OPERACAO || "", 
            DD_NUMERO_ROTA: row.original.DD_NUMERO_ROTA || "", 
            DD_CTE: row.original.DD_CTE  ? 1 : 0,   
            DD_EXIBIR_OPERACAO: row.original.DD_EXIBIR_OPERACAO  ? 1 : 0,   
            DD_LETRA_PEDIDO: row.original.DD_LETRA_PEDIDO || "",
            DT_VIGENCIA_INICIAL: row.original.DT_VIGENCIA_INICIAL != null ? new Date(`${row.original.DT_VIGENCIA_INICIAL} 00:00:00`) : new Date(),
            DT_VIGENCIA_FINAL: row.original.DT_VIGENCIA_FINAL != null ? new Date(`${row.original.DT_VIGENCIA_FINAL} 00:00:00`) : new Date(),
        })
    }, [row.original])

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: updateOperation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: operationKeys.lists() })
            queryClient.invalidateQueries({ queryKey: operationSegmentKeys.details() })
            toast({
                title: 'Operação atualizado com sucesso',
            })
            onClose()
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível editar a Operação. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao editar a Operação',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    }) 
    
    const onSubmit = (data: z.infer<typeof schema>) => {
        mutate({
            SK_OPERACAO: row.original.SK_OPERACAO,
           ...data
        })
    }

    const handleChange = (e) => {
        let value = e.target.value;

        // Remove tudo que não for número ou dois pontos
        value = value.replace(/[^0-9:]/g, '');

        // Adiciona ":" automaticamente após dois dígitos
        if (value.length > 2 && value.indexOf(':') === -1) {
        value = value.substring(0, 2) + ':' + value.substring(2);
        }

        // Validação do formato HH:mm
        const parts = value.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);

        // Limita horas a 99
        if (parts[0].length > 2 || hours > 99) {
        value = '99';
        }

        // Limita minutos a 59
        if (parts[1] && (parts[1].length > 2 || minutes > 59)) {
        value = parts[0] + ':59';
        }

        return value;
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[800px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Editar Operação</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                name="SK_FORNECEDOR"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Fornecedor - Origem</FormLabel> 
                                            {dataSupplier && (
                                                <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        className="justify-start w-full gap-2 text-xs"
                                                    >
                                                        {supplier === 0 ? (
                                                            <TypographyXS className="flex-1 text-start">
                                                                Selecione um Fornecedor
                                                            </TypographyXS>
                                                        ) : (
                                                            <>
                                                                <TypographyXS className="overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                                    {dataSupplier.find(supp => supp.SK_FORNECEDOR == supplier)?.NM_RAZAO_SOCIAL_CNPJ}
                                                                </TypographyXS>
                                                            </>
                                                        )}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-50 p-0" align="start">
                                                    <VirtualizedCommand   
                                                        options={dataSupplier.map(item => {return {value: item.SK_FORNECEDOR.toString(), label: item.NM_RAZAO_SOCIAL_CNPJ }})} 
                                                        placeholder="Fornecedor"   
                                                        selectedValues={{[`${field.value}`]: true}}
                                                        onSelectionChange={(data)=> { 
                                                            setSupplier(Number(data))
                                                            field.onChange(Number(data))
                                                        }}
                                                    />
                                                </PopoverContent>
                                                </Popover>
                                            )}  
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="SK_EMPRESA_DESTINO"
                                control={form.control}
                                render={({ field }) => (
                                        <FormItem>
                                        <FormLabel>Cliente - Destino</FormLabel>
                                        <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value.toString()}>
                                            <FormControl>
                                                <SelectTrigger>
                                                <SelectValue placeholder="" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {dataCompanies?.map((companies) => (
                                                    <SelectItem key={companies.SK_EMPRESA} value={companies.SK_EMPRESA}>{companies.DS_ABREVIATURA}</SelectItem>
                                                ))} 
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />  
                        </div>
                        <div className='grid grid-cols-3 gap-4'>
                            <FormField
                                name="DD_NUMERO_ROTA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Número Rota</FormLabel>
                                        <FormControl>
                                            <input {...field} type='text' maxLength={4} className='flex h-9 w-full text-foreground rounded-md bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_TEMPO_OPERACAO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Tempo de Operação</FormLabel>
                                        <FormControl>
                                            <input {...field} type='text' onChange={(e) => { 
                                                    field.onChange(handleChange(e))
                                                }} placeholder="00:00" maxLength={6} className='flex h-9 w-full text-foreground rounded-md bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_LETRA_PEDIDO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Letra Pedido</FormLabel>
                                        <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent> 
                                                <SelectItem value="B">B</SelectItem>
                                                <SelectItem value="F">F</SelectItem>
                                                <SelectItem value="G">G</SelectItem> 
                                                <SelectItem value="I">I</SelectItem> 
                                                <SelectItem value="T">T</SelectItem> 
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                name="DT_VIGENCIA_INICIAL"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                        <FormLabel>Vigência Incial</FormLabel>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                            <FormControl>
                                                <Button
                                                variant={"outline"}
                                                className={cn(
                                                    "pl-3 text-left font-normal",
                                                    !field.value && "text-muted-foreground"
                                                )}
                                                >
                                                {field.value ? (
                                                    format(field.value, "PPP")
                                                ) : (
                                                    <span>Selecionar Vigência Incial</span>
                                                )}
                                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                </Button>
                                            </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={field.value}
                                                onSelect={field.onChange} 
                                                initialFocus
                                            />
                                            </PopoverContent>
                                        </Popover> 
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                name="DT_VIGENCIA_FINAL"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                        <FormLabel>Vigência Final</FormLabel>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                            <FormControl>
                                                <Button
                                                variant={"outline"}
                                                className={cn(
                                                    "pl-3 text-left font-normal",
                                                    !field.value && "text-muted-foreground"
                                                )}
                                                >
                                                {field.value ? (
                                                    format(field.value, "PPP")
                                                ) : (
                                                    <span>Selecionar Vigência Final</span>
                                                )}
                                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                </Button>
                                            </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={field.value}
                                                onSelect={field.onChange} 
                                                initialFocus
                                            />
                                            </PopoverContent>
                                        </Popover> 
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />  
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name="DD_CTE"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 shadow">
                                        <FormControl>
                                            <Checkbox
                                                checked={Boolean(field.value)}
                                                onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                                            />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                            <FormLabel>
                                                Operação com CTE?
                                            </FormLabel> 
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="DD_EXIBIR_OPERACAO"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 shadow">
                                        <FormControl>
                                            <Checkbox
                                                checked={Boolean(field.value)}
                                                onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                                            />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                            <FormLabel>
                                                Exibir Operação no Cockpit?
                                            </FormLabel> 
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default EditDialog
